import { PlainButton, PrimaryButton } from "@digipost/dds-buttons-react";
import { Heading } from "@digipost/dds-heading-react";
import Infobox from "@digipost/dds-infobox-react";
import { Spinner } from "@digipost/dds-spinner-react";
import { useLanguage } from "@digipost/translate";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { ValidationRules } from "../../../types/entrypoint";
import InfoWarningText from "../InfoWarningText";
import { Dispatch } from "../infrastructure/redux/actions";
import {
  completeRegistrationAction,
  RegistrationStatus,
  setUserInputAction,
} from "../infrastructure/redux/org/org-actions";
import { ReduxState } from "../infrastructure/redux/state";
import Translated from "../infrastructure/Translated";
import { useRootPath } from "../infrastructure/useRootPath";
import { useTranslate } from "../infrastructure/useTranslate";
import ContactForm from "./ContactForm";
import { isAlreadyRegistered } from "./domain";
import GoToDigipostLogInButton from "./GoToDigipostLogInButton";
import OrganisationChoice from "./OrganisationChoice";
import styles from "./SigneringRegisterOrganisationForm.module.css";
import { TrueFalseRadioButton } from "./TrueFalseRadioButton";

const SigneringRegisterOrganisationForm: FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [validations, setValidations] = useState({ email: true, phone: true });

  const userInput = useSelector((state: ReduxState) => state.org.userInput);
  const validationRules = useSelector(
    (state: ReduxState) => state.app.validationRules,
  );
  const orgs = useSelector((state: ReduxState) => state.org.data);
  const registrationStatus = useSelector(
    (state: ReduxState) => state.org.registrationStatus,
  );
  const registrableOrganisations = orgs?.registrableOrganisations ?? [];

  const dispatch = useDispatch<Dispatch>();
  const rootPath = useRootPath();

  const isValidFormValue = (
    value: string,
    required: boolean,
    type: keyof ValidationRules,
  ): boolean => {
    const validationRule = validationRules[type];
    const emptyAndRequired = !value && required;
    return (
      !emptyAndRequired &&
      (!validationRule || !value || RegExp(validationRule).test(value))
    );
  };

  const onSubmit = (event: React.ChangeEvent<HTMLFormElement>): void => {
    const validEmail = isValidFormValue(userInput.email, true, "email");
    const validPhone = isValidFormValue(userInput.phone, true, "phoneNumber");

    setValidations({ phone: validPhone, email: validEmail });

    if (validEmail && validPhone) {
      dispatch(completeRegistrationAction(true, false));
    }

    event.preventDefault();
  };

  const selectedOrganisation = orgs?.registrableOrganisations.find(
    (p) => p.organisationNumber === userInput.selectedOrganisationNumber,
  );
  const selectedOrganisationCanBeRegistered =
    selectedOrganisation && !isAlreadyRegistered(selectedOrganisation);
  const chosenOrgRegisteredInDigipost =
    selectedOrganisation && isAlreadyRegistered(selectedOrganisation);

  return (
    <div>
      <div className="register-content-wrapper">
        <form className="register-content-wrapper" onSubmit={onSubmit}>
          <Heading level={1} size="large">
            <Translated textKey="register.org.registration_form_signering.header" />
          </Heading>

          <div>
            <Heading level={2} size="medium" className={styles.heading2}>
              {registrableOrganisations.length > 1 ? (
                <Translated textKey="register.org.registration_form_signering.choose" />
              ) : (
                <Translated textKey="register.org.registration_form_signering.your_organisation" />
              )}
            </Heading>

            <fieldset className={styles.radioGroup}>
              {registrableOrganisations.map((org) => {
                const isSelected =
                  org.organisationNumber ===
                  userInput.selectedOrganisationNumber;
                return (
                  <OrganisationChoice
                    key={org.organisationNumber}
                    onChange={(orgNumber) =>
                      dispatch(
                        setUserInputAction({
                          selectedOrganisationNumber: orgNumber,
                        }),
                      )
                    }
                    isSelected={isSelected}
                    {...org}
                  >
                    {isSelected && isAlreadyRegistered(org) && (
                      <InfoWarningText
                        className={styles.warning}
                        simple={true}
                        textkey="register.org.registration_form.already_registered_in_dp_but_not_signering"
                      />
                    )}
                  </OrganisationChoice>
                );
              })}
            </fieldset>
          </div>

          <div className="skjema_alert">
            <FontAwesomeIcon icon={faExclamationCircle} />
            <Translated textKey="register.org.manual.form.missing_org_signering" />{" "}
            <a
              href={`/registrering/${language}/bedrift/manuell?redirect-url-signering=https%3A%2F%2Fsignering.posten.no%2Fvirksomhet%2Flogginn%2Fprivat%3Fsource%3Dfullført-registrering`}
              data-testid="bedrift-manual-registration-link"
            >
              <Translated textKey="register.org.manual.form.missing_org_link" />
            </a>
          </div>

          {!chosenOrgRegisteredInDigipost && (
            <div>
              <Heading level={2} size="medium" className={styles.heading2}>
                <Translated textKey="register.org.signering_motta_digipost.header" />
              </Heading>
              <p>
                <Translated textKey="register.org.signering_motta_digipost.desc" />
              </p>
              <TrueFalseRadioButton
                name="postmottak"
                value={userInput.mailbox}
                change={(val) =>
                  dispatch(
                    setUserInputAction({
                      mailbox: val.target.value === "true",
                    }),
                  )
                }
              />
            </div>
          )}

          {!chosenOrgRegisteredInDigipost && (
            <div>
              <Heading level={2} size="medium" className={styles.heading2}>
                <Translated textKey="register.org.registration_form.contact_information" />
              </Heading>
              <ContactForm
                emailOnChange={(email) =>
                  dispatch(setUserInputAction({ email }))
                }
                phoneOnChange={(phone) =>
                  dispatch(setUserInputAction({ phone }))
                }
                email={userInput.email}
                phone={userInput.phone}
                emailValid={validations.email}
                phoneValid={validations.phone}
              />
            </div>
          )}

          <div role="alert">
            {registrationStatus === RegistrationStatus.FAILED && (
              <Infobox
                className={styles.errorBox}
                title={translate("register.org.error.general_header")}
                variant="error"
                level={2}
              />
            )}
          </div>

          <div className={styles.buttons}>
            <PlainButton
              type="button"
              onClick={() => navigate(`${rootPath}/bedrift`)}
            >
              <Translated textKey="generic.cancel" />
            </PlainButton>

            {chosenOrgRegisteredInDigipost ? (
              <GoToDigipostLogInButton />
            ) : (
              <PrimaryButton
                type="submit"
                disabled={
                  registrationStatus === RegistrationStatus.SUBMITTING ||
                  !selectedOrganisation ||
                  !selectedOrganisationCanBeRegistered
                }
                iconRight={
                  registrationStatus === RegistrationStatus.SUBMITTING && (
                    <Spinner />
                  )
                }
                data-testid="complete-registration-button"
              >
                <Translated textKey="register.complete" />
              </PrimaryButton>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SigneringRegisterOrganisationForm;
